import React from "react";
import { Link } from "react-router-dom";

const HeroSection = ({ 
  title = "Uniting Ojotorians Across North America",
  subtitle = "Fostering unity, development, and cultural preservation. Join our community of professionals, students, and leaders making a difference in our hometown and diaspora.",
  buttons = [
    { text: "Apply Now", link: "/register", variant: "blue" },
    { text: "Login", link: "/login", variant: "blue" }
  ],
  heroImage = null,
  collageImages = null
}) => {
  const defaultCollageImages = collageImages || [];
  
  // Build a collage with fewer visible repeats.
  const fillCollage = (images, targetCount, seedLabel = "") => {
    if (images.length === 0) return [];
    const uniqueImages = Array.from(new Set(images));
    const hash = Array.from(seedLabel).reduce((acc, ch) => acc + ch.charCodeAt(0), 0) || 1;
    const sorted = [...uniqueImages].sort((a, b) => {
      const aScore = (a.length * 17 + hash) % 97;
      const bScore = (b.length * 17 + hash) % 97;
      return aScore - bScore;
    });

    const filled = [];
    for (let i = 0; i < targetCount; i++) {
      let candidate = sorted[i % sorted.length];
      if (sorted.length > 1 && filled[i - 1] === candidate) {
        candidate = sorted[(i + 1) % sorted.length];
      }
      filled.push(candidate);
    }
    return filled;
  };

  return (
    <section 
      className="relative w-full overflow-hidden"
      style={{ 
        height: '85vh',
        minHeight: '600px',
        maxHeight: '900px'
      }}
    >
      {/* Collage Background - No gaps, covers entire hero, filled completely */}
      {defaultCollageImages.length > 0 ? (
        <>
          {/* Desktop: 5 columns, fill with enough images */}
          <div 
            className="hidden md:grid absolute inset-0"
            style={{
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 0
            }}
          >
            {fillCollage(defaultCollageImages, 30, `${title}-desktop`).map((img, index) => (
              <div
                key={index}
                className="relative overflow-hidden"
                style={{
                  width: '100%',
                  height: '100%'
                }}
              >
                <img
                  src={img}
                  alt={`Community member ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </div>
            ))}
          </div>
          {/* Mobile: 2 columns, fill with enough images */}
          <div 
            className="md:hidden grid absolute inset-0"
            style={{
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 0
            }}
          >
            {fillCollage(defaultCollageImages, 24, `${title}-mobile`).map((img, index) => (
              <div
                key={index}
                className="relative overflow-hidden"
                style={{
                  width: '100%',
                  height: '100%'
                }}
              >
                <img
                  src={img}
                  alt={`Community member ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </div>
            ))}
          </div>
        </>
      ) : heroImage ? (
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Ojoto Union"
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-800"></div>
      )}

      {/* Desktop: Smaller Semi-transparent White Overlay - Left Aligned */}
      <div 
        className="hidden md:flex absolute inset-y-0 items-center"
        style={{
          left: '8%',
          padding: '32px'
        }}
      >
        <div 
          className="w-full"
          style={{
            background: 'rgba(255, 255, 255, 0.85)',
            padding: '36px',
            borderRadius: '8px',
            maxWidth: '500px'
          }}
        >
          {/* H1 - Reduced font size */}
          <h1 
            className="mb-3"
            style={{
              fontFamily: "'Open Sans', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: '32px',
              lineHeight: 1.2,
              color: '#1F2937'
            }}
          >
            {title}
          </h1>
          
          {/* Blue horizontal line */}
          <div 
            className="mb-3"
            style={{
              width: '100px',
              height: '2px',
              backgroundColor: '#1E40AF'
            }}
          ></div>
          
          {/* Subtitle - Reduced font size */}
          <p 
            className="mb-6"
            style={{
              fontFamily: "'Open Sans', system-ui, sans-serif",
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: 1.6,
              color: '#374151',
              maxWidth: '480px'
            }}
          >
            {subtitle}
          </p>
          
          {/* CTA Buttons - Only Apply Now and Login */}
          <div className="flex flex-wrap gap-3">
            {buttons.map((button, index) => {
              // Special styling for scholarship button
              const isScholarshipButton = button.text.toLowerCase().includes('scholarship');
              return (
                <Link 
                  key={index}
                  to={button.link}
                  className={`font-semibold transition-all inline-block ${
                    isScholarshipButton 
                      ? 'hover:scale-105 hover:shadow-xl' 
                      : 'hover:opacity-90'
                  }`}
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif",
                    backgroundColor: isScholarshipButton 
                      ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #F59E0B 100%)'
                      : '#1E40AF',
                    background: isScholarshipButton 
                      ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #F59E0B 100%)'
                      : '#1E40AF',
                    color: isScholarshipButton ? '#1F2937' : '#FFFFFF',
                    padding: isScholarshipButton ? '14px 28px' : '10px 20px',
                    borderRadius: '6px',
                    fontSize: isScholarshipButton ? '16px' : '15px',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    fontWeight: isScholarshipButton ? 700 : 600,
                    boxShadow: isScholarshipButton 
                      ? '0 4px 15px rgba(245, 158, 11, 0.4), 0 0 20px rgba(245, 158, 11, 0.2)' 
                      : 'none',
                    border: isScholarshipButton ? '2px solid #F59E0B' : 'none',
                    textTransform: isScholarshipButton ? 'uppercase' : 'none',
                    letterSpacing: isScholarshipButton ? '0.5px' : 'normal'
                  }}
                >
                  {button.text}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile: Full width overlay, centered text */}
      <div 
        className="md:hidden absolute inset-0 flex items-center justify-center px-6"
        style={{
          background: 'rgba(255, 255, 255, 0.85)'
        }}
      >
        <div className="w-full text-center">
          <h1 
            className="mb-4"
            style={{
              fontFamily: "'Open Sans', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: '28px',
              lineHeight: 1.2,
              color: '#1F2937'
            }}
          >
            {title}
          </h1>
          
          <div 
            className="mb-4 mx-auto"
            style={{
              width: '80px',
              height: '2px',
              backgroundColor: '#1E40AF'
            }}
          ></div>
          
          <p 
            className="mb-8"
            style={{
              fontFamily: "'Open Sans', system-ui, sans-serif",
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: 1.6,
              color: '#374151'
            }}
          >
            {subtitle}
          </p>
          
          <div className="flex flex-col gap-3 items-center">
            {buttons.map((button, index) => {
              // Special styling for scholarship button
              const isScholarshipButton = button.text.toLowerCase().includes('scholarship');
              return (
                <Link 
                  key={index}
                  to={button.link}
                  className={`font-semibold transition-all inline-block ${
                    isScholarshipButton 
                      ? 'hover:scale-105 hover:shadow-xl' 
                      : 'hover:opacity-90'
                  }`}
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif",
                    backgroundColor: isScholarshipButton 
                      ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #F59E0B 100%)'
                      : '#1E40AF',
                    background: isScholarshipButton 
                      ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #F59E0B 100%)'
                      : '#1E40AF',
                    color: isScholarshipButton ? '#1F2937' : '#FFFFFF',
                    padding: isScholarshipButton ? '16px 32px' : '12px 24px',
                    borderRadius: '6px',
                    fontSize: isScholarshipButton ? '17px' : '16px',
                    textDecoration: 'none',
                    width: isScholarshipButton ? 'auto' : '200px',
                    fontWeight: isScholarshipButton ? 700 : 600,
                    boxShadow: isScholarshipButton 
                      ? '0 4px 15px rgba(245, 158, 11, 0.4), 0 0 20px rgba(245, 158, 11, 0.2)' 
                      : 'none',
                    border: isScholarshipButton ? '2px solid #F59E0B' : 'none',
                    textTransform: isScholarshipButton ? 'uppercase' : 'none',
                    letterSpacing: isScholarshipButton ? '0.5px' : 'normal'
                  }}
                >
                  {button.text}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
